import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { defineMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import { publishUIMessage } from 'Common/notifications';
import { handleErrorNotification } from 'Common/utils';
import { useApiClient } from 'utils/apiClient';

export const useUsers = (tableParams: any) => {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: ['contacts', 'users', tableParams],
    queryFn: () => apiClient.Contacts.fetchUsers(tableParams),
    staleTime: 1000,
  });
};

export const useUserRoles = (tableParams?: any) => {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['contacts', 'roles', tableParams],
    queryFn: () => apiClient.Contacts.fetchUserRoles(tableParams),
    staleTime: 1000,
    refetchOnWindowFocus: true,
  });
};

export const useCreateUser = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: apiClient.Contacts.saveUser,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          'contacts',
          'users',
          { pagination: { current: 1, pageSize: 25 }, search: null },
        ],
      });
      publishUIMessage(
        'success',
        defineMessage({
          defaultMessage: 'User successfully saved',
        })
      );
    },
  });
};

export const useCreateRoles = () => {
  const apiClient = useApiClient();
  const intl = useIntl();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: apiClient.Contacts.saveRoles,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['contacts', 'roles', { search: null }],
      });
      publishUIMessage(
        'success',
        defineMessage({
          defaultMessage: 'Role successfully saved.',
        })
      );
    },
    onError: (error) => {
      handleErrorNotification({
        error,
        lang: intl.locale.toLowerCase(),
      });
    },
  });
};

export const useRemoveUser = () => {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const intl = useIntl();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: apiClient.Contacts.removeUser,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          'contacts',
          'users',
          { pagination: { current: 1, pageSize: 25 }, search: null },
        ],
      });
      navigate('/contacts/users?_=1');
      publishUIMessage(
        'success',
        defineMessage({
          defaultMessage: 'User successfully deleted',
        })
      );
    },
    onError: (error) => {
      handleErrorNotification({
        error,
        lang: intl.locale.toLowerCase(),
      });
    },
  });
};

export const useRemoveRole = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const intl = useIntl();
  return useMutation({
    mutationFn: apiClient.Contacts.removeRole,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['contacts', 'roles', { search: null }],
      });
      publishUIMessage(
        'success',
        defineMessage({
          defaultMessage: 'Role successfully deleted.',
        })
      );
    },
    onError: (error) => {
      handleErrorNotification({
        error,
        lang: intl.locale.toLowerCase(),
      });
    },
  });
};
